<template>
  <div>
    <b-col class="p-3 box-media" :class="{ 'box-media__required': required && !$hasFile }">
      <div class="h5">{{ header }}</div>
      <div class="text-black-50 mb-4">Only 1 file available at a time</div>
      <b-row>
        <b-col class="mb-3" style="max-width: max-content">
          <div class="img-content">
            <img
              class="rounded"
              style="max-width: 170px; max-height: 100px"
              height="auto"
              :src="dataURL ? dataURL : require('@/assets/default-activity-cover.jpg')"
              alt=""
            />
          </div>
        </b-col>
        <b-col
          class="d-flex flex-column justify-content-around mb-3"
          style="max-width: max-content"
        >
          <b-button
            variant="outline-primary"
            :disabled="disabled"
            @click="$refs.uploadS3Uploader.$refs.vueDropzone.$el.click()"
            >Upload Media</b-button
          >

          <b-button variant="link" :disabled="disabled" @click="resetUpload"
            >Reset to default</b-button
          >
        </b-col>
      </b-row>

      <div class="text-black-50">(Supported File Types: {{ acceptedFiles.toUpperCase() }})</div>

      <slot name="description" />

      <div v-if="isVideo">
        <base-s3-uploader
          v-show="false"
          id="input-media"
          ref="uploadS3Uploader"
          class="mt-2"
          hide-dropzone
          :deleteable="false"
          :reference-meta="{ ...referenceMeta, field: fieldUpload }"
          :dropzone-options="dropzoneOptions"
          :is-public="true"
          @onFilesAdded="onFilesAdded"
          @onError="onError"
        >
        </base-s3-uploader>

        <div class="d-flex">
          <BaseVideoPlayer
            v-if="isVideo && $file[0].video_link"
            id="previewVideo"
            :video="$file[0]"
            class="mt-3 container__preview-video"
            :options="videoOptions"
          />
        </div>

        <div
          v-if="
            isVideo && $file[0].video_link && !$file[0].isVideoAvailable && !$file[0].sourceFile
          "
          class="text-dark border p-4 rounded d-flex justify-content-between mt-3"
        >
          <div>Video is preparing to upload, please wait...</div>
        </div>
      </div>

      <div v-else>
        <base-s3-uploader
          v-if="!isLoading"
          id="input-media"
          ref="uploadS3Uploader"
          v-model="$file"
          class="mt-2"
          hide-dropzone
          hide-remove
          :reference-meta="{ ...referenceMeta, field: fieldUpload }"
          :dropzone-options="dropzoneOptions"
          :is-public="true"
          @onFilesAdded="onFilesAdded"
          @onError="onError"
        >
        </base-s3-uploader>
      </div>

      <slot name="progress-bar" />
    </b-col>
    <div v-if="required && !$hasFile" id="text-media__required" class="text-media__required">
      The Media is a required field.
    </div>
  </div>
</template>

<script>
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
import BaseVideoPlayer from '@/components/base-components/BaseVideoPlayer';
import Upload from '@/models/Upload';

export default {
  components: {
    BaseS3Uploader,
    BaseVideoPlayer
  },

  props: {
    referenceMeta: {
      type: Object,
      default: () => ({})
    },

    fieldUpload: {
      type: String,
      default: ''
    },

    header: {
      type: String,
      default: 'Upload media'
    },

    disabled: {
      type: Boolean,
      default: false
    },

    file: {
      type: Array,
      default: () => []
    },

    acceptedFiles: {
      type: String,
      default: '.jpeg, .jpg, .png, .gif, .mp4, .mov'
    },

    required: {
      type: Boolean,
      default: false
    },

    hasFile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      buffer: [],
      dataURL: '',
      isVideo: false,
      dropzoneOptions: {
        acceptedFiles: this.acceptedFiles,
        maxFiles: 1,
        maxfilesexceeded: function (file) {
          this.removeAllFiles();
          this.addFile(file);
        }
      },
      videoOptions: {
        responsive: false,
        transparent: true
      },
      isLoading: false
    };
  },

  computed: {
    $file: {
      get() {
        return this.file;
      },

      set(v) {
        this.$emit('update:file', v);
      }
    },

    $hasFile: {
      get() {
        return this.hasFile;
      },

      set(v) {
        this.$emit('update:hasFile', v);
      }
    }
  },

  watch: {
    file: {
      immediate: true,
      async handler(f) {
        if (f && f.length) {
          const file = f[0];

          if (file.video_id) {
            this.dataURL = file.thumbnail_link || require('@/assets/default-video-cover.jpg');
          } else if (file.mime && file.mime.startsWith('image/')) {
            const { response } = await Upload.api().fetchPresignedUrl(file.id);

            const { url } = response.data;

            this.dataURL = url;
          } else {
            this.dataURL = require('@/assets/default-activity-cover.jpg');
          }

          this.$hasFile = true;
        }
      }
    }
  },
  methods: {
    resetUpload() {
      if (this.file && this.file.length) {
        this.$refs.uploadS3Uploader.$refs.vueDropzone.removeAllFiles(this.file);
      } else {
        this.$refs.uploadS3Uploader.$refs.vueDropzone.removeAllFiles();
      }
      this.dataURL = '';
      this.$hasFile = false;
      this.isVideo = false;
      this.$emit('upload:video', null);
    },

    onFilesAdded(files) {
      const file = files[0];

      if (file && file.status == 'error') return;

      this.isVideo = false; //clear state show preview video
      this.$hasFile = true;

      if (file && (file.type == 'video/mp4' || file.type == 'video/quicktime')) {
        this.dataURL = require('@/assets/default-video-cover.jpg');
        return this.$emit('upload:video', file);
      } else {
        this.$emit('upload:video', null);
      }

      const reader = new FileReader();

      reader.addEventListener(
        'load',
        () => {
          // convert image file to base64 string
          this.dataURL = reader.result;
        },
        false
      );

      if (file) {
        reader.readAsDataURL(file);
      }
    },

    onError(message) {
      console.error(message);
      if (message == "You can't upload files of this type.") {
        this.dataURL = '';
        this.$hasFile = false;
      }
    },
    resetUploader() {
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
      }, 100);
    }
  }
};
</script>

<style lang="scss">
#previewVideo.container__preview-video iframe {
  max-height: 30vw !important;
  max-width: 100% !important;
  background-color: black !important;
}

.img-content {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center !important;
  width: 170px !important;
  height: 100px !important;
}

.text-media__required {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}
</style>
